/**
 * The app shell service worker and "Add to Home Screen". The worker caches the app itself and its
 * static files so Hondo opens without signal; ranch data stays in the API and the saved copies in
 * lib/offline. Install uses the browser's own prompt where there is one, and plain steps on iPhone.
 */
import { useSyncExternalStore } from "react";

interface BeforeInstallPromptEvent extends Event { prompt(): Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> }

export interface InstallState {
  /** The browser offered to install and we can show its prompt. */
  canPrompt: boolean;
  /** Already running as an installed app. */
  installed: boolean;
  /** iPhone or iPad Safari: no prompt, but Share > Add to Home Screen works. */
  ios: boolean;
  /** The service worker controls this page, so the app opens without signal. */
  offlineReady: boolean;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function detect(): InstallState {
  if (typeof window === "undefined") return { canPrompt: false, installed: false, ios: false, offlineReady: false };
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const ua = navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
  return { canPrompt: !!deferred, installed: !!standalone, ios, offlineReady: !!navigator.serviceWorker?.controller };
}

let state = detect();
const refresh = () => { state = detect(); listeners.forEach((l) => l()); };

export function useInstall(): InstallState {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => state, () => state);
}

/** Show the browser's install prompt. Resolves true when the person accepted. */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  const event = deferred;
  deferred = null;
  await event.prompt();
  const choice = await event.userChoice.catch(() => ({ outcome: "dismissed" as const }));
  refresh();
  return choice.outcome === "accepted";
}

/** Register the app shell worker. Skipped under the Vite dev server, where files change on every save. */
export function registerAppShell(): void {
  if (typeof window === "undefined" || !("serviceWorker" in navigator) || import.meta.hot) return;
  const register = () => {
    navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" }).then(() => navigator.serviceWorker.ready).then(refresh).catch(() => { /* The app still works online. */ });
  };
  // Wait for the page to finish loading so the worker's first downloads don't compete with it.
  if (document.readyState === "complete") register(); else window.addEventListener("load", register, { once: true });
  navigator.serviceWorker.addEventListener?.("controllerchange", refresh);
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e as BeforeInstallPromptEvent; refresh(); });
  window.addEventListener("appinstalled", () => { deferred = null; refresh(); });
}
